import { Trans, useLingui } from '@lingui/react';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Stack } from '@startuptools/ui/Stack';
import { Input } from '@startuptools/ui/Input';
import SearchIcon from '@mui/icons-material/Search';
import {
  type GqlOptionsSharesSubscriber,
  useCreateOptionsSharesSubscriberRepresentativeMutation,
  useDeleteOptionsSharesSubscriberRepresentativeMutation,
  useOssDeleteMutation,
  useOssSetPaymentStatusMutation,
} from '../../../../../../graphql/react-operations';
import * as React from 'react';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { ErrorDisplay } from '../../../../../../components/error-display/ErrorDisplay';
import { StepOsrSubscribersDocument, useStepOsrSubscribersSuspenseQuery } from './options-subscribers.graphql';
import { OptionsSharesSubscriberDialog } from './OptionsSharesSubscriberDialog';
import { OptionsSubscriber } from './OptionsSubscriber';

export const StepOsrSubscribers = ({ osrId }: { osrId: string }) => {
  const [addSubscriber, setAddSubscriber] = React.useState(false);
  const [editSubscriber, setEditSubscriber] = React.useState<Pick<
    GqlOptionsSharesSubscriber,
    'id' | 'identityId' | 'quantity' | 'shareClass'
  > | null>();
  const [searchTerm, setSearchTerm] = React.useState('');
  const companyId = useCompanyId();
  const {
    data: {
      optionsSharesRegistration: { subscribers, warrantProgram, ...osr },
      optionsSharesRegistrationErrors,
    },
  } = useStepOsrSubscribersSuspenseQuery({ variables: { companyId, osrId }, fetchPolicy: 'cache-and-network' });
  // eslint-disable-next-line sut/loading-mutation
  const [setPaymentStatus] = useOssSetPaymentStatusMutation({ refetchQueries: [StepOsrSubscribersDocument] });
  // eslint-disable-next-line sut/loading-mutation
  const [deleteSubscriber] = useOssDeleteMutation({ refetchQueries: [StepOsrSubscribersDocument] });
  // eslint-disable-next-line sut/loading-mutation
  const [deleteRepresentative] = useDeleteOptionsSharesSubscriberRepresentativeMutation({
    refetchQueries: [StepOsrSubscribersDocument],
  });
  // eslint-disable-next-line sut/loading-mutation
  const [createRepresentative] = useCreateOptionsSharesSubscriberRepresentativeMutation({
    refetchQueries: [StepOsrSubscribersDocument],
  });
  const { i18n } = useLingui();

  return (
    <>
      <Box>
        <Stack gap={2}>
          <Stack direction="row" justifyContent="space-between">
            <Button onClick={() => setAddSubscriber(true)}>
              <Trans id="Add subscriber" />
            </Button>
            <Input
              startDecorator={<SearchIcon />}
              placeholder={i18n._('Search subscriber')}
              value={searchTerm}
              onChange={event => {
                setSearchTerm(event.target.value);
              }}
            />
          </Stack>
          <ErrorDisplay error={optionsSharesRegistrationErrors.participants?.error} />
          <ErrorDisplay error={optionsSharesRegistrationErrors.options?.error} />
          <Stack gap={2} mt={2}>
            {subscribers
              .filter(s => {
                if (!searchTerm) return true;
                return s.identity.name.toLowerCase().includes(searchTerm.toLowerCase());
              })
              .sort((a, b) => a.identity.name.toLowerCase().localeCompare(b.identity.name.toLowerCase()))
              .map(subscriber => {
                return (
                  <OptionsSubscriber
                    key={subscriber.id}
                    editSubscriber={() => setEditSubscriber(subscriber)}
                    warrantProgram={warrantProgram}
                    companyId={companyId}
                    subscriber={subscriber}
                    setPaymentStatus={setPaymentStatus}
                    deleteSubscriber={deleteSubscriber}
                    createRepresentative={createRepresentative}
                    deleteRepresentative={deleteRepresentative}
                  />
                );
              })}
          </Stack>
        </Stack>
      </Box>
      {(addSubscriber || editSubscriber) && (
        <OptionsSharesSubscriberDialog
          warrantProgram={warrantProgram}
          optionsSharesRegistration={osr}
          oss={editSubscriber}
          onClose={() => {
            setAddSubscriber(false);
            setEditSubscriber(null);
          }}
        />
      )}
    </>
  );
};
